import * as Sentry from "@sentry/react";
import { decrypt } from "../utils/other";
import { logoutUser } from "../utils/auth";
import {
  AUTH_EVENTS,
  isLeavingPage,
  logAuthEventBeforeLeaving,
} from "../utils/authEventLog";

export const SESSION_EXPIRED_FLAG = "cy_session_expired";
export const SESSION_EXPIRED_MESSAGE =
  "Session expired, please login again.";

/**
 * Terminal session-expiry / unauthorized handler.
 *
 * With multiple login flows the old swal "Continue working?" re-login popup
 * (which POSTed to the legacy api/login/student) no longer makes sense. We now
 * simply: log the user out (clears storage + shared cookies, without cy-sso
 * api/logout: see logoutUser), leave a one-shot marker so the next page load shows a
 * "session expired" toast, then redirect to the unified login page.
 *
 * The marker survives the full-page redirect; the toast is fired on boot from
 * src/index.js (see showSessionExpiredToast).
 */
// Set once a session is being ended, so a request that 401s in the moment
// before the redirect doesn't end (and log) it a second time.
let sessionEnding = false;

/**
 * @param {string} [reason]  LOGOUT_REASONS value naming the call site
 * @param {object} [details] extra fields for the AUTH_SESSION_EXPIRED log
 */
const sessionExpired = (reason = "unknown", details = {}) => {
  if (sessionEnding) return;
  // A voluntary logout is already clearing the session and redirecting: a
  // request that 401s meanwhile must not turn it into "Session expired". Only
  // while nothing is stored, so any session that exists is always ended.
  if (isLeavingPage() && !localStorage.getItem("user")) return;
  sessionEnding = true;

  // Built now, while the session is still in storage; sent in the background.
  logAuthEventBeforeLeaving(AUTH_EVENTS.SESSION_EXPIRED, {
    reason,
    ...details,
  });

  try {
    const user = decrypt(localStorage.getItem("user")) || {};
    // A normal end of a session is not an error: the AUTH_SESSION_EXPIRED log
    // above records it, so Sentry gets a breadcrumb for the trail of a later
    // event, not an issue. Id only: Sentry data must not carry the student's
    // email or name.
    if (user.userId) {
      Sentry.addBreadcrumb({
        category: "auth",
        level: "info",
        message: "Session Expired",
        data: { userId: user.userId },
      });
    }
  } catch (_) {
    /* ignore */
  }

  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1");
  } catch (_) {
    /* ignore */
  }

  Sentry.setUser(null);
  logoutUser({ notifyServer: false });
  // The log already went out with keepalive, so nothing waits for it.
  window.location.replace("/");
};

export default sessionExpired;
